import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import CustomTabs from "../../../../../../../components/app/CustomTabs";
import { ChartActionStrip } from "../../../../../../../components/app/provider/patient/chart";
import { useRoleController } from "../../../../../../../components/useRoleController";
import { useReduceDisclosure } from "../../../../../../../hooks/use-reduce-disclosure";
import { roleFilter } from "../../../../../../../utils/utils";
import PastMedicalHistoryNew from "./PastMedicalHistory";
import PastSurgicalHistoryNew from "./PastSurgicalHistory";
import PatientFamilyHistoryNew from "./PatientFamilyHistory";
import SocialHistoryNew from "./SocialHistoryNew";
import UpsertFamilyHistory from "./UpsertFamilyHistory";
import UpsertMedicalHistoryDrawer from "./UpsertMedicalHistoryDrawer";
import UpsertSurgicalHistoryDrawer from "./UpsertSurgicalHistoryDrawer";

const HistoryLayoutNew = () => {
  const [medicationDisclosure, surgicalDisclosure, familyHistoryDisclosure] =
    useReduceDisclosure(4);
  const patientData: any = useOutletContext();

  const navigate = useNavigate();
  const isVisitNotePage = window.location.pathname.includes("/visit-note");

  const defTabPath = location.pathname.split("/").pop();
  const defTab =
    defTabPath === "history" || isVisitNotePage ? "" : (defTabPath as string);
  const [activeButton, setActiveButton] = useState<string>(defTab);

  useEffect(() => {
    setActiveButton(defTab);
  }, [defTab]);

  const { hasView: hasViewMedical } = useRoleController("Past Medical History");
  const { hasView: hasViewSurgical } = useRoleController("Surgical History");
  const { hasView: hasViewFamily } = useRoleController("Family History");
  const { hasView: hasViewSocial } = useRoleController("Social history");

  const endContentMap: Record<string, ReactNode> = {
    "": (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => medicationDisclosure.open()}
      >
        Add Past Medical History
      </ChartActionStrip.Button>
    ),
    surgical: (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => surgicalDisclosure.open()}
      >
        Add Past Surgical History
      </ChartActionStrip.Button>
    ),
    family: (
      <ChartActionStrip.Button
        startIcon={<AddIcon />}
        onClick={() => familyHistoryDisclosure.open()}
      >
        Add Family History
      </ChartActionStrip.Button>
    ),
  };

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <UpsertMedicalHistoryDrawer disclosureHost={medicationDisclosure} />
      <UpsertSurgicalHistoryDrawer disclosureHost={surgicalDisclosure} />
      <UpsertFamilyHistory disclosureHost={familyHistoryDisclosure} />

      <ChartActionStrip
        startContent={
          <CustomTabs
            tabsData={roleFilter({
              list: TABS_DATA,
              compWith: {
                "": hasViewMedical,
                surgical: hasViewSurgical,
                family: hasViewFamily,
                social: hasViewSocial,
              },
            })}
            value={activeButton}
            onChange={(v) => {
              setActiveButton(v);
              if (!isVisitNotePage) navigate(v);
            }}
          />
        }
        endContent={endContentMap[activeButton]}
      />

      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {isVisitNotePage ? (
          ActiveContentComponent[activeButton]
        ) : (
          <Outlet context={patientData} />
        )}
      </Box>
    </Box>
  );
};

export default HistoryLayoutNew;

const ActiveContentComponent: Record<string, ReactNode> = {
  "": <PastMedicalHistoryNew />,
  surgical: <PastSurgicalHistoryNew />,
  family: <PatientFamilyHistoryNew />,
  social: <SocialHistoryNew />,
};

const TABS_DATA = [
  {
    value: "",
    label: "Past Medical History",
  },
  {
    value: "surgical",
    label: "Past Surgical History",
  },
  {
    value: "family",
    label: "Family History",
  },
  {
    value: "social",
    label: "Social History",
  },
];
